import { AIAssistantPanel, PANEL_MODES, usePanelMode } from '/b2b/common/components/AIAssistant';
import { Backdrop, Divider, Drawer, useMediaQuery } from '@mui/material';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { Logo as OsanoLogo, Square as OsanoLogoSquare } from '/b2b/common/icons/Osano';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import AIAssistantFab from './components/AIAssistantFab';
import AuthenticatedPaths from '../../../routing/AuthenticatedRoutes/AuthenticatedRoutes.paths';
import IconButton from '@mui/material/IconButton';
import MobileTopbar from './components/MobileTopbar';
import OrgSwitcher from './components/OrgSwitcher';
import PropTypes from 'prop-types';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { SIDE_PANEL_WIDTH } from '/b2b/common/components/AIAssistant/constants';
import Scrollbar from '../../components/Scrollbar';
import Sidebar from './components/Sidebar';
import SidebarFooter from './components/SidebarFooter';
import UnauthenticatedPaths from '../../../routing/UnauthenticatedRoutes/UnauthenticatedRoutes.paths';
import { addIdToElements } from '../../helpers/DOM';
import { createCounterObject } from '../../helpers/Number';
import createCustomMutationObserver from '../../helpers/CustomMutationObserver';
import { makeStyles } from 'tss-react/mui';
import styles from './DashboardLayout.styles';
import { translate } from '/b2b/common/helpers/i18n';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '/b2b/App.context';

const useStyles = makeStyles()(styles);

// Scroll clearance so content can sit clear of the floating AI launcher in the
// bottom-right corner (tuned to the button's footprint).
const FAB_SCROLL_GUTTER = 56;

// Hover-to-peek timing for the collapsed rail (ms).
const PEEK_OPEN_DELAY = 400;
const PEEK_CLOSE_DELAY = 150;

const DashboardLayout = props => {
    const {
        blackout,
        canViewAiAssistant,
        children,
        customerId,
        drawerOpen,
        hasMultipleCustomers,
        onAuthChange,
        onCloseDrawer,
        onToggleDrawer,
        permissionsDetermined,
        userId,
    } = props;
    const { classes, cx } = useStyles();
    const theme = useTheme();
    const { toggleSidebarPinned } = useThemeContext();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const [scrollShadow, setScrollShadow] = useState({ top: false, bottom: false });
    const [peeking, setPeeking] = useState(false);
    const peekTimerRef = useRef(null);

    const handleScrollUpdate = useCallback(scrollValues => {
        const { scrollTop = 0, scrollHeight = 0, clientHeight = 0 } = scrollValues || {};
        const top = scrollTop > 0;
        const bottom = Math.ceil(scrollTop + clientHeight) < scrollHeight;
        setScrollShadow(prev =>
            prev.top === top && prev.bottom === bottom ? prev : { top, bottom }
        );
    }, []);

    const panelMode = usePanelMode();
    const isSidePanelOpen = canViewAiAssistant && panelMode === PANEL_MODES.SIDEBAR;
    // The AI launcher floats bottom-right whenever the assistant isn't already
    // open in a panel; reserve a matching scroll gutter so it never permanently
    // covers page content in the corner.
    const isFabVisible =
        canViewAiAssistant &&
        panelMode !== PANEL_MODES.SIDEBAR &&
        panelMode !== PANEL_MODES.WIDGET &&
        panelMode !== PANEL_MODES.FULLSCREEN;

    const pinned = theme.ui.sidebarPinned;
    // Collapsed rail expands on hover/focus ("peek") without shifting content;
    // pinning keeps it expanded and pushes content over.
    const expanded = isDesktop && (pinned || peeking);
    const displayCollapsed = isDesktop && !expanded;
    const shiftContent = permissionsDetermined && isDesktop;

    const handleTogglePin = () => {
        const next = !pinned;
        toggleSidebarPinned(next);
        if (next) {
            setPeeking(false);
        }
    };

    const clearPeekTimer = () => {
        if (peekTimerRef.current) {
            clearTimeout(peekTimerRef.current);
            peekTimerRef.current = null;
        }
    };

    const startPeek = () => {
        if (pinned || !isDesktop) return;
        clearPeekTimer();
        peekTimerRef.current = setTimeout(() => setPeeking(true), PEEK_OPEN_DELAY);
    };

    const endPeek = () => {
        if (pinned || !isDesktop) return;
        clearPeekTimer();
        peekTimerRef.current = setTimeout(() => setPeeking(false), PEEK_CLOSE_DELAY);
    };

    // Hovering an open sidebar menu (which portals outside the rail) keeps the
    // rail expanded immediately, so moving onto the menu doesn't collapse it.
    const keepPeekAlive = () => {
        if (pinned || !isDesktop) return;
        clearPeekTimer();
        setPeeking(true);
    };

    const handleRailFocus = () => {
        if (pinned || !isDesktop) return;
        clearPeekTimer();
        setPeeking(true);
    };

    const handleRailBlur = event => {
        if (pinned || !isDesktop) return;
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setPeeking(false);
        }
    };

    useEffect(() => {
        return () => {
            if (peekTimerRef.current) {
                clearTimeout(peekTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (pinned || !isDesktop) {
            setPeeking(false);
        }
    }, [pinned, isDesktop]);

    const contentClass = useMemo(
        () =>
            cx(classes.content, {
                [classes.contentShift]: shiftContent && pinned,
                [classes.contentShiftMini]: shiftContent && !pinned,
            }),
        [classes.content, classes.contentShift, classes.contentShiftMini, shiftContent, pinned]
    );

    const allPaths = {
        ...AuthenticatedPaths,
        ...UnauthenticatedPaths,
    };

    const location = useLocation();
    const prefix =
        Object.values(allPaths).find(
            value =>
                matchPath(location.pathname, {
                    path: value,
                }) && value
        ) || location.pathname;
    useEffect(() => {
        customerId && userId && onAuthChange();
    }, [customerId, userId]);

    const mainRef = useRef(null);
    const topBarRef = useRef(null);
    const drawerRef = useRef(null);

    useLayoutEffect(() => {
        const PATH_PREFIX = prefix.slice(1).replace(/-|\//g, '_');

        if (process.env.MODE !== 'prod') {
            const ELEMENT_TYPES_TO_ADD_ID = ['input', 'button', 'textarea', 'label', 'a'];
            const ELEMENTS_TO_OBSERVE = [
                { refElement: mainRef.current, prefixName: PATH_PREFIX },
                { refElement: topBarRef.current, prefixName: 'topbar' },
                { refElement: drawerRef.current, prefixName: 'drawer' },
            ]
                // Not all elements are visible at all resolutions
                .filter(({ refElement }) => !!refElement);
            const mutationObserverObjects = ELEMENTS_TO_OBSERVE.map(elementToObserve => {
                const elementCounterObj = createCounterObject(ELEMENT_TYPES_TO_ADD_ID);
                const { refElement, prefixName } = elementToObserve;
                return createCustomMutationObserver(refElement, () =>
                    addIdToElements(refElement, elementCounterObj, prefixName)
                );
            });

            return () => mutationObserverObjects.forEach(observer => observer.disconnect());
        }
    }, [location.pathname]);

    return (
        <div className={classes.root}>
            {!isDesktop && <MobileTopbar ref={topBarRef} onToggleSidebar={onToggleDrawer} />}
            <Drawer
                anchor="left"
                classes={{
                    root: classes.drawerRoot,
                    paper: cx(classes.drawerPaper, {
                        collapsed: displayCollapsed,
                        peeking: peeking && !pinned,
                    }),
                }}
                onClose={onCloseDrawer} // for mobile drawer
                open={permissionsDetermined && (isDesktop || drawerOpen)}
                variant={isDesktop ? 'persistent' : 'temporary'}
                ref={drawerRef}
                PaperProps={{
                    onMouseEnter: startPeek,
                    onMouseLeave: endPeek,
                    onFocus: handleRailFocus,
                    onBlur: handleRailBlur,
                }}
            >
                {/* One header row in every state: the mark stays fixed at the
                    left while the org name, dropdown, and pin slide in as the rail
                    widens. The org switcher itself is clipped by the rail width. */}
                <div className={classes.logoWrapper}>
                    {hasMultipleCustomers ? (
                        <OrgSwitcher
                            collapsed={displayCollapsed}
                            onFlyoutEnter={keepPeekAlive}
                            onFlyoutLeave={endPeek}
                        />
                    ) : (
                        <Link className={classes.logoLink} to="/">
                            {displayCollapsed ? (
                                <OsanoLogoSquare className={classes.logoSquare} alt="Osano logo" />
                            ) : (
                                <OsanoLogo className={classes.logo} alt="Osano logo" />
                            )}
                        </Link>
                    )}
                    {!displayCollapsed && isDesktop && (
                        <IconButton
                            className={classes.drawerToggle}
                            onClick={handleTogglePin}
                            aria-label={translate(pinned ? 'common.unpin' : 'common.pin')}
                        >
                            {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                    )}
                </div>
                <Divider className={classes.sectionDivider} />
                <div className={classes.scrollRegion}>
                    <div
                        className={cx(classes.scrollShadowTop, {
                            [classes.scrollShadowVisible]: scrollShadow.top,
                        })}
                    />
                    <Scrollbar
                        data-testid="drawerScroll"
                        className={classes.drawerScroller}
                        noScrollX
                        dark
                        onUpdate={handleScrollUpdate}
                    >
                        <Sidebar className={classes.sidebar} collapsed={displayCollapsed} />
                    </Scrollbar>
                    <div
                        className={cx(classes.scrollShadowBottom, {
                            [classes.scrollShadowVisible]: scrollShadow.bottom,
                        })}
                    />
                </div>
                <SidebarFooter
                    minified={displayCollapsed}
                    onFlyoutEnter={keepPeekAlive}
                    onFlyoutLeave={endPeek}
                />
            </Drawer>
            <main ref={mainRef} className={contentClass}>
                <Scrollbar
                    data-testid="contentScroll"
                    className={classes.panel}
                    contentPaddingRight={isSidePanelOpen ? SIDE_PANEL_WIDTH : 0}
                    contentPaddingBottom={isFabVisible ? FAB_SCROLL_GUTTER : 0}
                >
                    {children}
                </Scrollbar>
            </main>
            <Backdrop open={blackout} classes={{ root: classes.blackout }} />
            {canViewAiAssistant && <AIAssistantPanel />}
            {canViewAiAssistant && <AIAssistantFab />}
        </div>
    );
};

DashboardLayout.propTypes = {
    blackout: PropTypes.bool,
    canViewAiAssistant: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    customerId: PropTypes.string,
    drawerOpen: PropTypes.bool,
    hasMultipleCustomers: PropTypes.bool,
    onAuthChange: PropTypes.func.isRequired,
    onCloseDrawer: PropTypes.func.isRequired,
    onToggleDrawer: PropTypes.func.isRequired,
    permissionsDetermined: PropTypes.bool.isRequired,
    userId: PropTypes.string,
};

export default DashboardLayout;
